/**
 * React Context + hook for managing editor preferences with backend persistence.
 *
 * Architecture:
 * - EditorPreferencesProvider: Single source of truth for preferences state
 * - useEditorPreferences: Hook that consumes the context
 * - Shared state across all consumers (FeedbackPanel + StressOverlayPlugin)
 *
 * Features:
 * - Loads preferences from backend on mount (merged with defaults)
 * - Deep merges updates to preserve nested structure
 * - Persists updates to backend (debounced 500ms)
 * - Optimistic UI updates with error handling
 * - Falls back to defaults if backend fails
 * - Supports local-only fields (not persisted, always reset to defaults on mount)
 *
 * Local-Only Fields:
 * - feedback.rhyme.showLabels: Session-only UI state, always defaults to true on mount
 *
 * The frontend does NOT use Supabase directly - all DB access goes through backend.
 */

import { useState, useCallback, useMemo, useEffect, useRef, createContext, useContext, type ReactNode } from 'react';
import { getDefaultPreferences } from '../types/preferences';
import type { EditorPreferences } from '../types/preferences';
import { backendApiClient } from '../lib/backendApi';

interface UseEditorPreferencesReturn {
  preferences: EditorPreferences;
  updatePreferences: (updates: Partial<EditorPreferences>) => void;
  loading: boolean;
  error: Error | null;
}

/**
 * Deep merge helper - recursively merges objects at all levels.
 * Needed because Object.assign/spread only does shallow merge.
 */
function deepMergeObject<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key as keyof T];
      const targetValue = target[key as keyof T];

      if (
        sourceValue !== null &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        // Recursively merge nested objects
        (result as Record<string, unknown>)[key] = deepMergeObject(
          targetValue as object,
          sourceValue as object
        );
      } else if (sourceValue !== undefined) {
        (result as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Type-safe wrapper for merging EditorPreferences.
 */
function deepMerge(
  target: EditorPreferences,
  source: Partial<EditorPreferences>
): EditorPreferences {
  return deepMergeObject(target, source);
}

/**
 * Filters out local-only preference fields that should never be saved to backend.
 * Local-only fields are reset to defaults on mount, so storing them serves no purpose.
 *
 * Currently strips:
 * - feedback.rhyme.showLabels (session-only UI state)
 *
 * Persisted fields:
 * - feedback.rhyme.suggestions.enabled (DEPRECATED - AI suggestions feature removed,
 *   retained for backward compatibility only, has no runtime effect)
 *
 * Returns a deep partial that excludes local-only fields. The backend API accepts
 * partial preferences, so we don't need to include fields we want to ignore.
 */
function stripLocalOnlyFields(prefs: EditorPreferences): Record<string, unknown> {
  // Build the backend payload without local-only fields
  // Using Record<string, unknown> because we're intentionally creating a partial
  // structure that omits local-only fields
  return {
    feedback: {
      stress: prefs.feedback.stress,
      rhyme: {
        enabled: prefs.feedback.rhyme.enabled,
        // showLabels is intentionally omitted - it's local-only
        suggestions: prefs.feedback.rhyme.suggestions, // Persist AI suggestion preference
      },
      section: prefs.feedback.section, // Section preferences ARE persisted
    },
    display: prefs.display,
  };
}

// Create Context - starts as null to enforce provider usage
const EditorPreferencesContext = createContext<UseEditorPreferencesReturn | null>(null);

/**
 * EditorPreferencesProvider - Manages shared preferences state with backend persistence.
 *
 * This provider MUST wrap all components that use preferences
 * (FeedbackPanel + StressOverlayPlugin) to ensure they share the same state.
 */
export function EditorPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<EditorPreferences>(getDefaultPreferences());
  const [loading, setLoading] = useState(true);  // Start as true for initial load
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load preferences from backend on mount
  useEffect(() => {
    let mounted = true;

    async function loadPreferences() {
      try {
        const serverPrefs = await backendApiClient.getUserPreferences();
        if (mounted) {
          const merged = deepMerge(getDefaultPreferences(), serverPrefs);
          // Reset local-only fields to defaults (ignore backend values)
          merged.feedback.rhyme.showLabels = getDefaultPreferences().feedback.rhyme.showLabels;
          // Section preferences ARE persisted - do NOT reset to defaults
          setPreferences(merged);
          setError(null);
        }
      } catch (err) {
        console.warn('Failed to load preferences, using defaults:', err);
        if (mounted) {
          setPreferences(getDefaultPreferences());
          // Surface error to UI while still using defaults (optimistic pattern)
          setError(err instanceof Error ? err : new Error('Failed to load preferences'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadPreferences();
    return () => { mounted = false; };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Debounced save to backend
  const debouncedSave = useCallback((prefs: EditorPreferences) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        // Strip local-only fields before sending to backend
        const backendPrefs = stripLocalOnlyFields(prefs);
        await backendApiClient.updateUserPreferences(backendPrefs);
        setError(null);
      } catch (err) {
        console.warn('Failed to save preferences:', err);
        setError(err instanceof Error ? err : new Error('Save failed'));
        // Preserve local state on save failure - don't revert
      } finally {
        setSaving(false);
      }
    }, 500); // 500ms debounce
  }, []);

  /**
   * Update preferences with deep merge and persist to backend.
   * Optimistic update - UI changes immediately, save happens in background.
   */
  const updatePreferences = useCallback((updates: Partial<EditorPreferences>) => {
    setPreferences(current => {
      const merged = deepMerge(current, updates);
      // Optimistic update - save happens in background
      debouncedSave(merged);
      return merged;
    });
  }, [debouncedSave]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    preferences,
    updatePreferences,
    loading: loading || saving,  // True during initial load OR save
    error,
  }), [preferences, updatePreferences, loading, saving, error]);

  return (
    <EditorPreferencesContext.Provider value={value}>
      {children}
    </EditorPreferencesContext.Provider>
  );
}

/**
 * useEditorPreferences - Hook to consume preferences context.
 *
 * MUST be used within EditorPreferencesProvider, otherwise throws error.
 * This ensures FeedbackPanel and StressOverlayPlugin share the same state.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useEditorPreferences(): UseEditorPreferencesReturn {
  const context = useContext(EditorPreferencesContext);

  if (!context) {
    throw new Error('useEditorPreferences must be used within EditorPreferencesProvider');
  }

  return context;
}
